<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import { useI18n } from 'vue-i18n'
import eventService from '../services/event.service'
import categoryService from '../services/category.service'
import coachService from '../services/coach.service'
import playerService from '../services/player.service'
import ParticipantSelectorV2 from '../components/ParticipantSelectorV2.vue'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

// Form data
const title = ref('')
const categoryType = ref('')
const place = ref('')
const startDate = ref('')
const startTime = ref('')
const endDate = ref('')
const endTime = ref('')
const eventDateParam = ref('') // Original event date from query param

// Participant selection
const selectedCoaches = ref<string[]>([])
const selectedPlayers = ref<string[]>([])
const pendingCategoryUuids = ref<string[]>([]) // categories from API, resolved after players/coaches load

// Data from API
const categories = ref<any[]>([])
const coaches = ref<any[]>([])
const players = ref<any[]>([])

// Loading states
const loading = ref(true)
const loadingCategories = ref(false)
const loadingCoaches = ref(false)
const loadingPlayers = ref(false)
const submitting = ref(false)

// Get event UUID and date from route
const eventUuid = computed(() => route.params.uuid as string)
const clubUuid = computed(() => authStore.initData?.club?.uuid || null)

// Category type options (from backend validation)
const categoryTypeOptions = [
  { value: 'training', label: t('editEvent.categoryTraining') },
  { value: 'match', label: t('editEvent.categoryMatch') },
  { value: 'other', label: t('editEvent.categoryOther') }
]

// Fetch event data
const fetchEvent = async () => {
  if (!clubUuid.value || !eventUuid.value) return

  loading.value = true
  try {
    const dateParam = route.query.date as string
    eventDateParam.value = dateParam

    const response = await eventService.getEvent(clubUuid.value, eventUuid.value, dateParam)

    // Fill form with event data
    title.value = response.title || ''
    categoryType.value = response.category_type || ''
    place.value = response.place || ''

    // Parse dates
    if (response.start) {
      const [startDatePart, startTimePart] = response.start.split(' ')
      startDate.value = startDatePart
      startTime.value = startTimePart
    }

    if (response.end) {
      const [endDatePart, endTimePart] = response.end.split(' ')
      endDate.value = endDatePart
      endTime.value = endTimePart
    }

    // Set selected participants
    selectedCoaches.value = response.coaches || []
    selectedPlayers.value = response.players || []
    pendingCategoryUuids.value = response.categories || []
  } catch (err: any) {
    console.error('Error fetching event:', err)
    toastStore.error(t('editEvent.errorFetchingEvent'))
    router.push('/events')
  } finally {
    loading.value = false
  }
}

// Fetch data
const fetchCategories = async () => {
  if (!clubUuid.value) return

  loadingCategories.value = true
  try {
    const response = await categoryService.getCategories(clubUuid.value)
    categories.value = response.data || []
  } catch (err: any) {
    console.error('Error fetching categories:', err)
    toastStore.error(t('editEvent.errorFetchingCategories'))
  } finally {
    loadingCategories.value = false
  }
}

const fetchCoaches = async () => {
  if (!clubUuid.value) return

  loadingCoaches.value = true
  try {
    const response = await coachService.getCoaches(clubUuid.value, 9999)
    coaches.value = response.data || []
  } catch (err: any) {
    console.error('Error fetching coaches:', err)
    toastStore.error(t('editEvent.errorFetchingCoaches'))
  } finally {
    loadingCoaches.value = false
  }
}

const fetchPlayers = async () => {
  if (!clubUuid.value) return

  loadingPlayers.value = true
  try {
    const response = await playerService.getPlayers(clubUuid.value, { per_page: 9999 })
    players.value = response.data?.players || []
  } catch (err: any) {
    console.error('Error fetching players:', err)
  } finally {
    loadingPlayers.value = false
  }
}

// Expand category UUIDs into individual player/coach UUIDs
const resolveCategories = () => {
  for (const categoryUuid of pendingCategoryUuids.value) {
    for (const player of players.value) {
      if (player.categories?.some((c: any) => c.uuid === categoryUuid) && !selectedPlayers.value.includes(player.uuid)) {
        selectedPlayers.value.push(player.uuid)
      }
    }
    for (const coach of coaches.value) {
      if (coach.categories?.some((c: any) => c.uuid === categoryUuid) && !selectedCoaches.value.includes(coach.uuid)) {
        selectedCoaches.value.push(coach.uuid)
      }
    }
  }
  pendingCategoryUuids.value = []
}

// Form submission
const handleSubmit = async () => {
  if (!clubUuid.value || !eventUuid.value) {
    toastStore.error(t('editEvent.errorNoClub'))
    return
  }

  if (!title.value || !categoryType.value || !startDate.value) {
    toastStore.error(t('editEvent.errorFillRequired'))
    return
  }

  submitting.value = true

  try {
    const data: any = {
      title: title.value,
      category_type: categoryType.value,
      start_date: startDate.value
    }

    if (place.value) data.place = place.value
    if (startTime.value) data.start_time = startTime.value
    if (endDate.value) data.end_date = endDate.value
    if (endTime.value) data.end_time = endTime.value
    if (eventDateParam.value) data.date = eventDateParam.value

    if (selectedCoaches.value.length > 0) {
      data.coaches = selectedCoaches.value
    }
    if (selectedPlayers.value.length > 0) {
      data.players = selectedPlayers.value
    }

    const response = await eventService.updateEvent(clubUuid.value, eventUuid.value, data)
    const message = response?.message || t('editEvent.eventUpdated')
    toastStore.success(message)

    router.push('/events')
  } catch (err: any) {
    console.error('Error updating event:', err)
    const errorMessage = err.response?.data?.message || t('editEvent.errorUpdatingEvent')
    toastStore.error(errorMessage)
  } finally {
    submitting.value = false
  }
}

const handleCancel = () => {
  router.push('/events')
}

onMounted(async () => {
  await Promise.all([
    fetchEvent(),
    fetchCategories(),
    fetchCoaches(),
    fetchPlayers()
  ])
  resolveCategories()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4 md:py-4 py-2">
      <!-- Back Button -->
      <div class="mb-4">
        <router-link to="/events" class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('editEvent.backToEvents') }}
        </router-link>
      </div>

      <!-- Page Header -->
      <div class="mb-4 md:mb-4 mb-2">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('editEvent.title') }}</h1>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <p class="mt-2 text-gray-600">{{ t('editEvent.loadingEvent') }}</p>
      </div>

      <!-- Form -->
      <div v-else class="bg-white border-2 border-gray-300 rounded shadow-sm p-6">
        <form @submit.prevent="handleSubmit">
          <!-- Basic Fields -->
          <div class="mb-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-4">{{ t('editEvent.basicInfo') }}</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- Event Name -->
              <div class="md:col-span-2">
                <label for="eventName" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editEvent.eventName') }} <span class="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="eventName"
                  v-model="title"
                  required
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Category Type -->
              <div class="md:col-span-2">
                <label for="categoryType" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editEvent.categoryType') }} <span class="text-red-600">*</span>
                </label>
                <select
                  id="categoryType"
                  v-model="categoryType"
                  required
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600 bg-white"
                >
                  <option value="">{{ t('editEvent.selectCategory') }}</option>
                  <option v-for="option in categoryTypeOptions" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </div>

              <!-- Start Date -->
              <div>
                <label for="startDate" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editEvent.startDate') }} <span class="text-red-600">*</span>
                </label>
                <input
                  type="date"
                  id="startDate"
                  v-model="startDate"
                  required
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Start Time -->
              <div>
                <label for="startTime" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editEvent.startTime') }}
                </label>
                <input
                  type="time"
                  id="startTime"
                  v-model="startTime"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- End Date -->
              <div>
                <label for="endDate" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editEvent.endDate') }}
                </label>
                <input
                  type="date"
                  id="endDate"
                  v-model="endDate"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- End Time -->
              <div>
                <label for="endTime" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editEvent.endTime') }}
                </label>
                <input
                  type="time"
                  id="endTime"
                  v-model="endTime"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Place -->
              <div class="md:col-span-2">
                <label for="place" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editEvent.place') }}
                </label>
                <input
                  type="text"
                  id="place"
                  v-model="place"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>
            </div>
          </div>

          <!-- Participants Section -->
          <div class="mb-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-4">{{ t('editEvent.selectParticipants') }}</h2>
            <div v-if="loadingCategories || loadingCoaches || loadingPlayers" class="flex items-center gap-2 py-2 text-sm text-gray-500">
              <div class="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
              Ładowanie uczestników...
            </div>
            <ParticipantSelectorV2
              v-else
              :categories="categories"
              :players="players"
              :coaches="coaches"
              v-model:selectedPlayers="selectedPlayers"
              v-model:selectedCoaches="selectedCoaches"
            />
          </div>

          <!-- Submit Buttons -->
          <div class="flex justify-end gap-2">
            <button
              type="button"
              @click="handleCancel"
              class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
            >
              {{ t('editEvent.cancel') }}
            </button>
            <button
              type="submit"
              :disabled="submitting"
              class="px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 border-2 border-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ submitting ? t('editEvent.saving') : t('editEvent.saveChanges') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </main>
</template>
